import {
    createContext,
    useContext,
    useState
} from 'react';

import { Conversation, Message, PromptLight } from './DataDefinitions';
import apiClient, { handleAPIError } from 'helpers/apiService';
import { useAuth } from 'providers/AuthProvider'

interface ChatState {
    currentConversation: null | Conversation;
}

interface ChatContextInterface extends ChatState {
    startNewChat: (prompt: PromptLight) => void;
    setCurrentConversation: (selectedConversation: Conversation) => void;
    sendMessage: (prompt: string) => void;
}

export const PromptsContext = createContext({} as ChatContextInterface);

const PromptsProvider = ({ children }: { children: React.ReactNode }) => {
    const initState: ChatState = {
        currentConversation: null,
    };

    const auth = useAuth();
    const [chatState, setChatState] = useState<ChatState>(initState);

    const startNewChat = async (prompt: PromptLight) => {
        const newConversation: Conversation = {
            conversation_id: 0,
            title: prompt.prompt_name,
            prompt: prompt,
            messages: [],
            inserted_at: new Date().toISOString() // Inserted date in ISO format
        };


        setChatState((prevState) => {
            return {
                ...prevState,
                currentConversation: newConversation
            };
        });
    };


    const setCurrentConversation = async (selectedConversation: Conversation) => {
        if (selectedConversation) {
            try {
                const response = await apiClient.get(
                    `/v1/va/${selectedConversation.conversation_id}`,
                    {
                        headers: {
                            'accept': 'application/json',

                        },
                        withCredentials: true,
                    }
                );
                selectedConversation.messages = response.data;
            } catch (error) {
                console.error('Error fetching data:', error);
            }

            setChatState((prevState) => ({
                ...prevState, // Keep the rest of the state unchanged
                currentConversation: selectedConversation,
            }));
        }

    };


    const addEmptyMessageToCurrentConversation = (prompt: string, callback?: (lastId: number) => void) => {
        setChatState((prevState) => {
            if (!prevState.currentConversation) {
                return prevState; // If there is no current conversation, do nothing
            }

            const newMessageUser: Message = {
                type: 'sent',
                time: new Date().toISOString(), // Current time in ISO format
                message: prompt, // Empty message content
                asking: false,
            };

            const newMessageAI: Message = {
                type: 'received',
                time: new Date().toISOString(), // Current time in ISO format
                message: "", // Empty message content
                asking: true,
            };

            const updatedConversation = {
                ...prevState.currentConversation,
                messages: [
                    ...prevState.currentConversation.messages, // Keep existing messages
                    newMessageUser,
                    newMessageAI // Add an empty message
                ]
            };

            return {
                ...prevState,
                currentConversation: updatedConversation,   // is updated with the new message            
            };
        });
    };


    const updateMessageWithApiResponse = (message: string) => {
        setChatState((prevState) => {
            if (!prevState.currentConversation) {
                return prevState; // If there is no current conversation, do nothing
            }

            const updatedMessages = prevState.currentConversation.messages.map((msg) => {
                if (msg.asking === true) {
                    return {
                        ...msg,
                        message: message, // Set the new message from the API response
                        asking: false,
                    };
                }
                return msg; // Return the message unchanged if it's not id=0
            });

            const updatedConversation: Conversation = {
                ...prevState.currentConversation,
                messages: updatedMessages, // Use the updated messages array
            };

            return {
                ...prevState,
                currentConversation: updatedConversation
            };
        });
    };

    const updateCurrentConversation = (newTitle: string, newId: number) => {
        setChatState((prevState) => {
            if (!prevState.currentConversation) {
                return prevState; // Do nothing if there is no current conversation
            }

            // Update the current conversation with the new title and id
            const updatedConversation: Conversation = {
                ...prevState.currentConversation,
                title: newTitle, // Set the new title
                conversation_id: newId,       // Set the new id
            };

            return {
                ...prevState,
                currentConversation: updatedConversation,
            };
        });
    };


    const sendMessage = async (prompt: string) => {

        addEmptyMessageToCurrentConversation(prompt);

        const formData = new FormData();
        formData.append('promptForm', JSON.stringify({ 'prompt': prompt, 'conversation': chatState.currentConversation }));
        try {
            const response = await apiClient.post(
                `/v1/prompt`, formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );

            updateMessageWithApiResponse(response.data['message']);
            //if (messageId === 1)
            if (response.data['update_conversation_on_FE'] === true) {
                updateCurrentConversation(prompt, response.data['conversation_id'])
            }

        } catch (error) {
            console.error('Request failed:', error);

        }
    }




    return (
        <PromptsContext.Provider
            value={{
                ...chatState,
                startNewChat,
                setCurrentConversation,
                sendMessage,
            }}
        >
            {children}
        </PromptsContext.Provider>
    );
};

export const useChatContext = () => useContext(PromptsContext);

export default PromptsProvider;
